import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row" }
const _hoisted_2 = { class: "col span-6" }

import { ref, watch, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import Banner from '@components/Banner/Banner.vue';
import { Checkbox } from '@components/Form/Checkbox';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import RcSelectOrCreateAuthSecret from '@shell/components/form/RcSelectOrCreateAuthSecret.vue';
import { MANAGEMENT } from '@shell/config/types';
import { SETTING } from '@shell/config/settings';
import { PRIVATE_REGISTRY_CONTEXT } from '@shell/components/form/PrivateRegistry.constants';
import type { PrivateRegistryContext } from '@shell/components/form/PrivateRegistry.constants';
import { RcContentGroup } from '@components/Layout';

export default /*@__PURE__*/_defineComponent({
  __name: 'RcPrivateRegistry',
  props: {
    value: { default: undefined },
    enabled: { type: Boolean, default: false },
    mode: { default: 'edit' },
    rules: { default: () => [] },
    checkboxTestId: { default: undefined },
    inputTestId: { default: undefined },
    pullSecret: { default: undefined },
    registerBeforeHook: {},
    context: { default: PRIVATE_REGISTRY_CONTEXT.PROVISIONING },
    defaultRegistry: { default: undefined },
    namespace: { default: 'fleet-default' },
    inStore: { default: 'management' },
    showPullSecrets: { type: Boolean, default: true },
    noneLabel: { default: undefined },
    skipPullSecrets: { type: Boolean, default: false },
    repoDefaultPullSecrets: { default: () => [] },
    existingValuesPullSecrets: { default: () => [] },
    authSecretDraft: { default: null }
  },
  emits: ["update:value", "update:enabled", "update:pullSecret", "update:skipPullSecrets", "update:authSecretDraft"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

// Respect `enabled` as well as `value`, otherwise remounting with the registry enabled but no URL yet unchecks the box
const showInput = ref(props.enabled || !!props.value);
const globalRegistry = ref('');
const defaultPullSecrets = ref<string[]>([]);
const localSkipPullSecrets = ref(props.skipPullSecrets);

const isCharts = computed(() => props.context === PRIVATE_REGISTRY_CONTEXT.CHARTS);
const isImporting = computed(() => props.context === PRIVATE_REGISTRY_CONTEXT.IMPORTING);

const descriptionKey = computed(() => {
  if (isCharts.value) {
    return 'catalog.chart.registry.tooltip';
  }
  if (isImporting.value) {
    return 'cluster.privateRegistry.importedDescription';
  }

  return 'cluster.privateRegistry.description';
});

const checkboxLabelKey = computed(() => {
  if (isCharts.value) {
    return 'catalog.chart.registry.custom.checkBoxLabel';
  }

  return 'cluster.privateRegistry.label';
});

const checkboxTooltipKey = computed(() => {
  if (isCharts.value) {
    return 'catalog.chart.registry.tooltip';
  }

  return undefined;
});

onMounted(() => {
  if (props.defaultRegistry) {
    globalRegistry.value = props.defaultRegistry;
  } else {
    const registrySetting = store.getters['management/byId'](MANAGEMENT.SETTING, SETTING.SYSTEM_DEFAULT_REGISTRY);

    globalRegistry.value = registrySetting?.value || registrySetting?.defaultValue;
  }

  const pullSecretsSetting = store.getters['management/byId'](MANAGEMENT.SETTING, SETTING.SYSTEM_DEFAULT_REGISTRY_PULL_SECRETS);

  if (props.repoDefaultPullSecrets?.length) {
    defaultPullSecrets.value = props.repoDefaultPullSecrets;
  } else if (pullSecretsSetting?.value ) {
    defaultPullSecrets.value = pullSecretsSetting?.value.split(',').map((s: string) => s.trim());
  }
});

const hasMultipleDefaultSecrets = computed(() => {
  return defaultPullSecrets.value?.length > 1;
});

/**
 * On upgrade, if the chart values already contain multiple imagePullSecrets,
 * the dropdown cannot represent them.  Show a banner instead and let the
 * user edit the values YAML directly.
 */
const hasMultipleExistingPullSecrets = computed(() => {
  return (props.existingValuesPullSecrets?.length ?? 0) > 1;
});

/**
 * Format a list of names with commas and "and" before the last item.
 * e.g. ["a"] => "<code>a</code>"
 *      ["a", "b"] => "<code>a</code> and <code>b</code>"
 *      ["a", "b", "c"] => "<code>a</code>, <code>b</code>, and <code>c</code>"
 */
function formatSecretList(names: string[]): string {
  const wrapped = names.map((n) => `<code>${ n }</code>`);

  const and = t('generic.and');

  if (wrapped.length <= 1) {
    return wrapped[0] || '';
  }
  if (wrapped.length === 2) {
    return `${ wrapped[0] }${ and }${ wrapped[1] }`;
  }

  return `${ wrapped.slice(0, -1).join(', ') },${ and }${ wrapped[wrapped.length - 1] }`;
}

/**
 * If there is exactly one default pull secret configured, we can show that it is the default in the secret dropdown.
 * If there is more than one, UI can't be certain which secret will be used
 * so a banner listing all secrets is shown instead.
 */
const defaultPullSecretLabel = computed(() => {
  if (props.noneLabel) {
    return props.noneLabel;
  }
  if (!defaultPullSecrets.value?.length) {
    return null;
  }
  if (defaultPullSecrets.value?.length === 1) {
    return t('catalog.chart.registry.pullSecret.defaultLabel', { name: defaultPullSecrets.value[0] });
  }

  return t('catalog.chart.registry.pullSecret.defaultLabelGeneric');
});

const existingValuesBannerHtml = computed(() => {
  if (!props.existingValuesPullSecrets?.length) {
    return '';
  }

  return t('catalog.chart.registry.pullSecret.existingValuesBanner', { secrets: formatSecretList(props.existingValuesPullSecrets) }, true);
});

const defaultSecretsBannerHtml = computed(() => {
  if (!defaultPullSecrets.value?.length) {
    return '';
  }

  return t('catalog.chart.registry.pullSecret.defaultSecretsBanner', { secrets: formatSecretList(defaultPullSecrets.value) }, true);
});

watch(() => props.enabled, (neu) => {
  if (typeof neu === 'boolean' && neu !== showInput.value) {
    showInput.value = neu;
  }
});

watch(showInput, (neu, old) => {
  if (neu !== props.enabled) {
    emit('update:enabled', neu);
  }
  if (!neu && old && props.value) {
    emit('update:value', undefined);
    emit('update:pullSecret', undefined);
  }
});

watch(() => props.value, (neu) => {
  if (!!neu && !showInput.value) {
    showInput.value = true;
  }
});

watch(() => props.pullSecret, (neu, old) => {
  if (neu && !props.value) {
    emit('update:value', globalRegistry.value);
  }
  if (!neu && old && props.value === globalRegistry.value) {
    emit('update:value', undefined);
  }
});

watch(localSkipPullSecrets, (neu) => {
  emit('update:skipPullSecrets', neu);
  if (neu) {
    emit('update:pullSecret', undefined);
  }
});

watch(() => props.skipPullSecrets, (neu) => {
  if (neu !== localSkipPullSecrets.value) {
    localSkipPullSecrets.value = neu;
  }
});


return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RcContentGroup), null, {
      default: _withCtx(() => [
        _createVNode(Banner, {
          color: "info",
          "label-key": descriptionKey.value,
          class: "m-0"
        }, null, 8, ["label-key"]),
        _cache[5] || (_cache[5] = _createTextVNode()),
        _createVNode(_unref(Checkbox), {
          value: showInput.value,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((showInput).value = $event)),
          mode: __props.mode,
          label: _unref(t)(checkboxLabelKey.value),
          tooltip: checkboxTooltipKey.value ? _unref(t)(checkboxTooltipKey.value) : undefined,
          "data-testid": __props.checkboxTestId
        }, null, 8, ["value", "mode", "label", "tooltip", "data-testid"])
      ]),
      _: 1
    }),
    _cache[10] || (_cache[10] = _createTextVNode()),
    (showInput.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(LabeledInput, {
                value: __props.value || globalRegistry.value,
                mode: __props.mode,
                rules: __props.rules,
                required: !globalRegistry.value,
                "label-key": "catalog.chart.registry.custom.inputLabel",
                "data-testid": __props.inputTestId,
                placeholder: _unref(t)('catalog.chart.registry.custom.placeholder'),
                "onUpdate:value": _cache[1] || (_cache[1] = (val) => emit('update:value', val))
              }, null, 8, ["value", "mode", "rules", "required", "data-testid", "placeholder"])
            ])
          ]),
          _cache[9] || (_cache[9] = _createTextVNode()),
          (__props.showPullSecrets)
            ? (_openBlock(), _createBlock(_unref(RcContentGroup), { key: 0 }, {
                default: _withCtx(() => [
                  (isCharts.value)
                    ? (_openBlock(), _createBlock(_unref(Checkbox), {
                        key: 0,
                        value: localSkipPullSecrets.value,
                        "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((localSkipPullSecrets).value = $event)),
                        mode: __props.mode,
                        label: _unref(t)('catalog.chart.registry.pullSecret.skipOption'),
                        "data-testid": "registry-skip-pull-secrets-checkbox"
                      }, null, 8, ["value", "mode", "label"]))
                    : _createCommentVNode("", true),
                  _cache[6] || (_cache[6] = _createTextVNode()),
                  (hasMultipleExistingPullSecrets.value && !localSkipPullSecrets.value)
                    ? (_openBlock(), _createBlock(Banner, {
                        key: 1,
                        color: "info"
                      }, {
                        default: _withCtx(() => [
                          _withDirectives(_createElementVNode("span", null, null, 512), [
                            [_directive_clean_html, existingValuesBannerHtml.value]
                          ])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _cache[7] || (_cache[7] = _createTextVNode()),
                  (!hasMultipleExistingPullSecrets.value && hasMultipleDefaultSecrets.value && !localSkipPullSecrets.value)
                    ? (_openBlock(), _createBlock(Banner, {
                        key: 2,
                        color: "info"
                      }, {
                        default: _withCtx(() => [
                          _withDirectives(_createElementVNode("span", null, null, 512), [
                            [_directive_clean_html, defaultSecretsBannerHtml.value]
                          ])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _cache[8] || (_cache[8] = _createTextVNode()),
                  _createElementVNode("div", {
                    class: _normalizeClass({'col span-6': !isCharts.value})
                  }, [
                    (!localSkipPullSecrets.value && !hasMultipleExistingPullSecrets.value)
                      ? (_openBlock(), _createBlock(RcSelectOrCreateAuthSecret, {
                          key: 0,
                          value: __props.pullSecret,
                          namespace: __props.namespace,
                          "allow-rke": !isCharts.value,
                          vertical: !isCharts.value,
                          "in-store": __props.inStore,
                          "limit-to-namespace": "",
                          "fixed-image-pull-secret": "",
                          "none-label": defaultPullSecretLabel.value,
                          "image-pull-secret-docker-json-url-config": __props.value || globalRegistry.value,
                          "register-before-hook": __props.registerBeforeHook,
                          "pre-select": __props.authSecretDraft ?? undefined,
                          "onUpdate:value": _cache[3] || (_cache[3] = (val) => emit('update:pullSecret', val)),
                          onInputauthval: _cache[4] || (_cache[4] = (val) => emit('update:authSecretDraft', val))
                        }, null, 8, ["value", "namespace", "allow-rke", "vertical", "in-store", "none-label", "image-pull-secret-docker-json-url-config", "register-before-hook", "pre-select"]))
                      : _createCommentVNode("", true)
                  ], 2)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ], 64))
}
}

})